import React from 'react';

import { Field } from '@/design-system/field';
import { Hint } from '@/design-system/hint';
import { Label } from '@/design-system/label';
import { Textarea } from '@/design-system/textarea';
import CostItemsEditor, {
  costTotal,
  type CostItemDraft,
} from '@/pages/ujp/components/CostItemsEditor';
import MoneyInput from '@/pages/ujp/components/MoneyInput';
import { formatRupiah } from '@/pages/ujp/format';

/**
 * "Biaya rute" in the Route Planner drawer (PLAN-ROUTE-PLAN-COST-ITEMS): the
 * usual costs of driving this route in the same six slots as the UJP step
 * Biaya (the shared `CostItemsEditor`), copied slot-for-slot into the wizard
 * when ops picks the plan, plus a free-text note shown in the wizard.
 */

export {
  costTotal,
  emptyCostDrafts,
  foldedCostNote,
  toCostDrafts,
  toCostItemsPayload,
  type CostItemDraft,
} from '@/pages/ujp/components/CostItemsEditor';

export const NOTES_MAX = 1000;

export interface RoutePlanCostItemsProps {
  value: CostItemDraft[];
  onChange: (rows: CostItemDraft[]) => void;
  notes: string;
  onNotesChange: (notes: string) => void;
  /** Fixed BBM amount as digits; '' = none (ASSESSMENT-ROUTE-PLAN-BBM-OVERRIDE). */
  bbmFixOverride: string;
  onBbmFixOverrideChange: (digits: string) => void;
  disabled?: boolean;
}

const RoutePlanCostItems: React.FC<RoutePlanCostItemsProps> = ({
  value,
  onChange,
  notes,
  onNotesChange,
  bbmFixOverride,
  onBbmFixOverrideChange,
  disabled,
}) => (
  <section className="space-y-3" data-testid="route-plan-costs">
    <div className="flex items-end justify-between gap-3 border-t border-stroke-soft-200 pt-4">
      <div>
        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
          Biaya rute
        </p>
        <p className="mt-1 text-xs text-text-sub-600">
          Biaya yang biasa keluar di rute ini; kosongkan yang tidak ada.
          Otomatis disalin ke step Biaya saat rute dipilih di UJP; tetap bisa
          diubah di sana.
        </p>
      </div>
      {costTotal(value) > 0 && (
        <p
          className="whitespace-nowrap text-sm font-semibold tabular-nums text-text-strong-950"
          data-testid="route-plan-costs-total"
        >
          Total {formatRupiah(costTotal(value), { dash: false })}
        </p>
      )}
    </div>

    <CostItemsEditor value={value} onChange={onChange} disabled={disabled} />

    <Field>
      <Label htmlFor="route-plan-bbm-override">Override BBM</Label>
      <MoneyInput
        id="route-plan-bbm-override"
        aria-label="Override BBM"
        value={bbmFixOverride}
        onChange={onBbmFixOverrideChange}
        disabled={disabled}
      />
      <Hint>
        Opsional. Kalau diisi, Override BBM di UJP otomatis aktif dengan nominal
        ini saat rute dipilih; kosongkan untuk pakai hitungan liter × harga.
      </Hint>
    </Field>

    <Field>
      <Label htmlFor="route-plan-notes">Catatan rute</Label>
      <Textarea
        id="route-plan-notes"
        value={notes}
        onChange={(e) => onNotesChange(e.target.value)}
        maxLength={NOTES_MAX}
        rows={3}
        placeholder="mis. Masuk lewat gerbang 3, parkir truk di sisi barat"
        disabled={disabled}
      />
      <Hint>Tampil di wizard UJP saat rute ini dipilih.</Hint>
    </Field>
  </section>
);

export default RoutePlanCostItems;
